import { customFetcher } from "./http";
import { getAuthorizedRequestConfig } from "./auth";

export type NftResponse = {
  id: string;
  collectionAddress: string;
  itemAddress: string;
  tokenId: string;
  network: string;
  ownerAddress: string | null;
  ownerWalletId: string | null;
  ownerUserId: string | null;
  metadataUri: string | null;
  name: string | null;
  description: string | null;
  previewImage: string | null;
  attributes: unknown | null;
  lastSyncAt: string | null;
  lastKnownLt: string | null;
  lastKnownTxHash: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListNftsResponse = {
  items: NftResponse[];
  nextCursor?: string;
};

export type ListNftsRequest = {
  walletId?: string;
  cursor?: string;
  limit?: number;
};

export type SyncNftsRequest = {
  walletId?: string;
};

const getConfig = () => getAuthorizedRequestConfig();

export const listNfts = async (params?: ListNftsRequest) =>
  customFetcher<ListNftsResponse>(
    {
      url: "/nfts",
      params,
    },
    getConfig(),
  );

export const syncNfts = async (payload?: SyncNftsRequest) =>
  customFetcher<{
    status: string;
    scope: "user" | "wallet";
    walletId?: string;
  }>(
    {
      url: "/nfts/sync",
      method: "POST",
      data: payload,
    },
    getConfig(),
  );

export const getNftById = async (nftId: string) =>
  customFetcher<NftResponse>(
    {
      url: `/nfts/${nftId}`,
    },
    getConfig(),
  );

// NFT Metadata types (TEP-64 compliant)
export interface NftMetadataAttribute {
  trait_type: string;
  value: string | number;
}

export interface NftMetadataResponse {
  name: string;
  description: string;
  image: string;
  external_url: string;
  attributes: NftMetadataAttribute[];
}

/**
 * Fetch NFT metadata from utility endpoint (public, no auth required)
 * @param tokenId - NFT token ID (usually phone number like "37122221234")
 */
export const getNftMetadata = async (tokenId: string): Promise<NftMetadataResponse> => {
  const response = await fetch(`/metadata/${tokenId}/metadata.json`);
  if (!response.ok) {
    throw new Error(`Failed to fetch NFT metadata: ${response.status}`);
  }
  return response.json();
};

/**
 * Get NFT image SVG URL (full image with background and pattern)
 * @param tokenId - NFT token ID
 */
export const getNftImageUrl = (tokenId: string): string => {
  return `/api/utility/nft/${tokenId}/image.svg`;
};

/**
 * Get NFT body SVG URL (chip only: base + inlay + symbol, no background)
 * @param tokenId - NFT token ID (phone number)
 */
export const getNftBodyUrl = (tokenId: string): string => {
  return `/api/utility/nft/${tokenId}/body.svg`;
};

/**
 * Get NFT background SVG URL (gradient + pattern, no chip)
 * @param tokenId - NFT token ID (phone number)
 */
export const getNftBackgroundUrl = (tokenId: string): string => {
  return `/api/utility/nft/${tokenId}/background.svg`;
};
